/**
 * Variantwissel op de PDP.
 *
 * Twee losse kiezers (Kleur-swatches en Maat-pills) of de gewone
 * variant-dropdown. Bij elke keuze zoeken we de variant die bij de gekozen
 * kleur én maat hoort, zetten het id in het verborgen veld en werken prijs,
 * adviesprijs, voorraadregel, koopknop, sticky koopbalk en galerijfoto bij.
 * Maten die niet in de gekozen kleur bestaan worden uitgeschakeld; kies je
 * een kleur waarin de huidige maat niet bestaat, dan springen we naar de
 * dichtstbijzijnde maat die er wél is. Het variant-id komt in de URL.
 */

const kiezer = document.querySelector('[data-variant-kiezer]');
const dataEl = document.querySelector('[data-pdp-varianten]');

if (kiezer && dataEl) {
  const varianten = JSON.parse(dataEl.textContent);
  const prijsEl = document.querySelector('[data-pdp-prijs]');
  const adviesEl = document.querySelector('[data-pdp-adviesprijs]');
  const kleurnaamEl = document.querySelector('[data-pdp-kleurnaam]');
  const maatnaamEl = document.querySelector('[data-pdp-maatnaam]');
  const maatHintEl = document.querySelector('[data-pdp-maat-hint]');
  const idEl = document.querySelector('[data-pdp-variant-id]');
  const voorraadEl = document.querySelector('[data-pdp-voorraad]');
  const voorraadTekstEl = document.querySelector('[data-pdp-voorraad-tekst]');
  const levertijdEl = document.querySelector('[data-pdp-levertijd]');
  const levertijdTekstEl = document.querySelector('[data-pdp-levertijd-tekst]');
  const atcEl = document.querySelector('[data-pdp-atc]');
  const stickyPrijsEl = document.querySelector('[data-pdp-sticky-prijs]');
  const stickyKnopEl = document.querySelector('[data-pdp-sticky-knop]');
  const track = document.querySelector('[data-pdp-track]');
  const rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // optie-posities (1-3) uit de sectie; 0 = deze optie bestaat niet
  const kleurIndex = Number(kiezer.dataset.kleurIndex ?? 0);
  const maatIndex = Number(kiezer.dataset.maatIndex ?? 0);
  const kleurVan = (v) => (kleurIndex ? v.opties?.[kleurIndex - 1] : null);
  const maatVan = (v) => (maatIndex ? v.opties?.[maatIndex - 1] : null);
  const maatGetal = (maat) => parseFloat(String(maat).replace(',', '.')) || 0;

  const toonVariant = (v) => {
    if (idEl) idEl.value = v.id;
    if (prijsEl) {
      prijsEl.textContent = v.prijs;
      prijsEl.classList.toggle('pdp__prijs--sale', Boolean(v.adviesprijs));
    }
    if (adviesEl) {
      adviesEl.hidden = !v.adviesprijs;
      adviesEl.textContent = v.adviesprijs ?? '';
      // de visually-hidden "adviesprijs"/"nu"-labels gaan mee aan en uit
      for (const label of document.querySelectorAll('[data-pdp-adviesprijs-label], [data-pdp-prijs-label]')) {
        label.hidden = !v.adviesprijs;
      }
    }
    if (kleurnaamEl) kleurnaamEl.textContent = kleurVan(v) ?? v.titel;
    if (maatnaamEl) maatnaamEl.textContent = maatVan(v) ?? '';
    if (stickyPrijsEl) stickyPrijsEl.textContent = v.prijs;

    if (voorraadEl && voorraadTekstEl) {
      voorraadTekstEl.textContent = v.beschikbaar
        ? voorraadEl.dataset.tekstOp
        : voorraadEl.dataset.tekstUit;
      voorraadEl.querySelector('.pdp__voorraad-dot')?.classList.toggle('pdp__voorraad-dot--uit', !v.beschikbaar);
    }

    // Levertijd per voorraadbron (metafield levering.bron uit de voorraadsync): 'extern' = alleen
    // bij de leverancier op voorraad, dan de langere levertijd en de bus als icoon (CSS wisselt
    // op de klasse). Ontbreekt het veld, dan eigen voorraad. Uitverkocht: regel weg.
    if (levertijdEl) {
      const extern = v.bron === 'extern';
      const tekst = (extern ? levertijdEl.dataset.tekstExtern : levertijdEl.dataset.tekstIntern) ?? '';
      levertijdEl.classList.toggle('pdp__levertijd--extern', extern);
      if (levertijdTekstEl) levertijdTekstEl.textContent = tekst;
      levertijdEl.hidden = !v.beschikbaar || !tekst;
    }

    if (atcEl) {
      atcEl.disabled = !v.beschikbaar;
      const tekst = atcEl.querySelector('.pdp__atc-tekst');
      if (tekst) tekst.textContent = v.beschikbaar ? atcEl.dataset.tekstKoop : voorraadEl?.dataset.tekstUit ?? '';
    }

    // sticky koopbalk (mobiel) volgt de beschikbaarheid van de gekozen variant
    if (stickyKnopEl) {
      stickyKnopEl.disabled = !v.beschikbaar;
      stickyKnopEl.textContent = v.beschikbaar
        ? stickyKnopEl.dataset.tekstKoop
        : stickyKnopEl.dataset.tekstUit;
    }

    // maat-afhankelijke specs (families met optie Maat): spec-tabel en maatkaart
    if (v.specs) {
      const s = v.specs;
      const zetSpec = (key, tekst) => {
        const rij = document.querySelector(`[data-pdp-spec-rij="${key}"]`);
        const dd = rij?.querySelector('[data-pdp-spec]');
        if (!rij || !dd) return;
        if (tekst) { dd.textContent = tekst; rij.hidden = false; } else { rij.hidden = true; }
      };
      zetSpec('wielmaat', s.wielmaat ? `${s.wielmaat} inch` : '');
      zetSpec('leeftijd', s.leeftijd);
      zetSpec('lengte', s.lengte);
      zetSpec('gewicht', s.gewicht ? `${String(s.gewicht).replace('.', ',')} kg` : '');
      zetSpec('remtype', s.remtype ? s.remtype.charAt(0).toUpperCase() + s.remtype.slice(1) : '');

      // regel onder de titel: "16 inch · 4-6 jaar"
      const quick = document.querySelector('[data-pdp-quickspec]');
      if (quick) {
        const delen = [s.wielmaat ? `${s.wielmaat} inch` : null, s.leeftijd || null].filter(Boolean);
        quick.textContent = delen.join(' · ');
        quick.hidden = delen.length === 0;
      }

      // "Geschikt voor"-regel in de kenmerken (families)
      const geschikt = document.querySelector('[data-pdp-geschikt]');
      if (geschikt) {
        if (s.leeftijd) {
          const tpl = s.lengte ? geschikt.dataset.tpl : geschikt.dataset.tplLeeftijd;
          geschikt.textContent = (tpl ?? '')
            .replace('[doelgroep]', geschikt.dataset.doelgroep ?? '')
            .replace('[leeftijd]', s.leeftijd)
            .replace('[lengte]', s.lengte ?? '');
          geschikt.hidden = false;
        } else {
          geschikt.hidden = true;
        }
      }

      const kaart = document.querySelector('[data-maatkaart]');
      if (kaart) {
        kaart.dataset.wielmaat = s.wielmaat ?? '';
        kaart.dataset.lengte = s.lengteRichtlijn ?? '';
        const range = kaart.querySelector('[data-pdp-fit-range]');
        if (range) {
          const tpl = s.lengteAfgeleid ? kaart.dataset.fitRangeRichtlijnTpl : kaart.dataset.fitRangeTpl;
          range.textContent = s.lengteRichtlijn ? (tpl ?? '').replace('[lengte]', s.lengteRichtlijn) : (s.leeftijd ?? '');
          if (s.lengteRichtlijn && s.leeftijd) {
            const span = document.createElement('span');
            span.className = 'pdp__maatkaart-leeftijd';
            span.textContent = `(${s.leeftijd})`;
            range.append(' ', span);
          }
        }
      }
    }

    // scroll de galerij naar de foto van deze variant
    if (track && v.mediaId) {
      const beeld = track.querySelector(`[data-media-id="${v.mediaId}"]`);
      if (beeld) track.scrollTo({ left: beeld.offsetLeft, behavior: rustig ? 'auto' : 'smooth' });
    }

    history.replaceState(null, '', `${location.pathname}?variant=${v.id}`);
  };

  // maat-pills bijwerken voor de gekozen kleur: bestaat niet → uit, uitverkocht → doorgestreept
  const werkMatenBij = (kleur) => {
    if (!maatIndex) return;
    let verborgen = 0;
    for (const label of kiezer.querySelectorAll('[data-pdp-maat-label]')) {
      const input = label.querySelector('input');
      const kandidaten = varianten.filter((v) => maatVan(v) === input.value && (!kleurIndex || kleurVan(v) === kleur));
      const bestaat = kandidaten.length > 0;
      const leverbaar = kandidaten.some((v) => v.beschikbaar);
      input.disabled = !bestaat;
      label.classList.toggle('pdp__maat--nvt', !bestaat);
      label.classList.toggle('pdp__kleur--uit', bestaat && !leverbaar);
      if (!bestaat) verborgen++;
    }
    if (maatHintEl) maatHintEl.hidden = verborgen === 0;
  };

  const gekozen = (optie) => kiezer.querySelector(`input[data-pdp-optie="${optie}"]:checked`)?.value ?? null;

  const zoekVariant = (kleur, maat) => varianten.find((v) =>
    (!kleurIndex || kleurVan(v) === kleur) && (!maatIndex || maatVan(v) === maat));

  const kiesMaat = (maat) => {
    const input = kiezer.querySelector(`input[data-pdp-optie="maat"][value="${CSS.escape(maat)}"]`);
    if (input) input.checked = true;
  };

  kiezer.addEventListener('change', (e) => {
    const optie = e.target.dataset.pdpOptie;
    if (!optie) {
      // gewone dropdown: value is het variant-id
      const v = varianten.find((x) => x.id === Number(e.target.value));
      if (v) toonVariant(v);
      return;
    }
    const kleur = gekozen('kleur');
    let maat = gekozen('maat');
    if (optie === 'kleur') werkMatenBij(kleur);

    let v = zoekVariant(kleur, maat);
    if (!v && maatIndex) {
      // deze maat bestaat niet in de nieuwe kleur: spring naar de dichtstbijzijnde maat
      const inKleur = varianten.filter((x) => !kleurIndex || kleurVan(x) === kleur);
      const doel = maatGetal(maat);
      inKleur.sort((a, b) => Math.abs(maatGetal(maatVan(a)) - doel) - Math.abs(maatGetal(maatVan(b)) - doel) || (b.beschikbaar - a.beschikbaar));
      v = inKleur[0];
      if (v) { maat = maatVan(v); kiesMaat(maat); }
    }
    if (v) toonVariant(v);
  });

  werkMatenBij(gekozen('kleur'));
}
